import React, { useState } from "react";
import { Card, Button, Form, Row, Col } from "react-bootstrap";
import CustomSelect from "views/Components/CustomSelect";
import { Trash2, Plus, GripVertical, Box, Copy } from "lucide-react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
} from "@mui/material";
import CreatableSelect from "react-select/creatable";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { Switch } from "components/ui/switch";
import { INPUT_TYPES } from "../../../services/constants";
import { toast } from "react-toastify";
import localization from "services/localization";
import CreatableSelectDnd from "views/Components/CreatableSelectDnd";
import { useThemeStore } from "context/useThemeStore";

const emptyVizProp = {
  inputType: "text",
  name: "",
  label: "",
  description: "",
  placeholder: "",
  required: false,
  options: [],
  showResourceSourceOptions: false,
  resourceType: null,
};

const RESOURCE_TYPE_OPTIONS = [
  { label: localization.all || "All", value: "all" },
  { label: "Rag Only", value: "rag" },
  { label: "NonRag Only", value: "nonRag" },
];

export function VizPropsBuilder({ vizProps, onChange }) {
  const [isAdding, setIsAdding] = useState(false);
  const [newField, setNewField] = useState({ ...emptyVizProp });
  const [expandedAccordion, setExpandedAccordion] = useState(null);
  const { theme } = useThemeStore();

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  };

  const addVizProp = () => {
    onChange([...vizProps, newField]);
    setNewField({ ...emptyVizProp });
    setIsAdding(false);
  };

  const removeVizProp = (index) =>
    onChange(vizProps.filter((_, i) => i !== index));

  const updateVizProp = (index, field, value) => {
    const updated = vizProps.map((p, i) => {
      if (i !== index) return p;

      let updatedField = { ...p, [field]: value };

      if (field === "inputType") {
        if (value === "select") {
          updatedField.options = p.options || [];
        } else {
          delete updatedField.options;
        }

        if (value === "file-uploader") {
          updatedField.resourceType = "rag";
        }
        if (value === "file-uploader") {
          updatedField.showResourceSourceOptions =
            p.showResourceSourceOptions ?? false;
          // updatedField.resourceType = p.resourceType ?? "all";
        }
      }

      return updatedField;
    });

    onChange(updated);
  };

  return (
    <Card className="mb-4 w-100 shadow-sm viz-card">
      <div>
        <div className="viz-card-header d-flex justify-content-between align-items-center px-4 py-3 border-bottom">
          <div className="d-flex align-items-center gap-2">
            <Box size={20} className="text-primary" />
            <h5 className="mb-0 font-semibold">
              Input Fields ({vizProps.length})
            </h5>
          </div>
          {!isAdding && (
            <Button
              variant="primary"
              className="d-flex align-items-center gap-2"
              onClick={() => setIsAdding(true)}
            >
              <Plus size={16} />
              Add Field
            </Button>
          )}
        </div>
        <Card.Body>
          {/* Add Field Form */}
          {isAdding && (
            <Card
              className="mb-3 viz-add-card"
              style={{ backgroundColor: "rgba(0,123,255,0.05)" }}
            >
              <Card.Body>
                <Row className="mb-3">
                  <Col md={6}>
                    <Form.Label className="bootstrap-label">
                      Input Type
                    </Form.Label>
                    <CustomSelect
                      options={INPUT_TYPES}
                      value={INPUT_TYPES.find(
                        (opt) => opt.value === newField.inputType,
                      )}
                      onChange={(opt) => {
                        const updatedField = {
                          ...newField,
                          inputType: opt.value,
                        };
                        if (opt.value === "file-uploader") {
                          updatedField.resourceType = "rag";
                        } else {
                          updatedField.resourceType = null;
                        }
                        setNewField(updatedField);
                      }}
                      placeholder="Select input type..."
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Label className="bootstrap-label">
                      Field Name
                    </Form.Label>
                    <Form.Control
                      disabled={newField.inputType === "student-list"}
                      value={
                        newField.inputType === "student-list"
                          ? "sub_members"
                          : newField.name
                      }
                      onChange={(e) => {
                        const value = e.target.value
                          .toLowerCase()
                          .replace(/\s+/g, "_")
                          .replace(/[^a-z0-9_]/g, "");

                        setNewField({
                          ...newField,
                          name: value,
                        });
                      }}
                      placeholder="field_name"
                    />
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col md={6}>
                    <Form.Label className="bootstrap-label">Label</Form.Label>
                    <Form.Control
                      value={newField.label}
                      onChange={(e) =>
                        setNewField({ ...newField, label: e.target.value })
                      }
                      placeholder="Display label"
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Label className="bootstrap-label">
                      Placeholder
                    </Form.Label>
                    <Form.Control
                      value={newField.placeholder}
                      onChange={(e) =>
                        setNewField({
                          ...newField,
                          placeholder: e.target.value,
                        })
                      }
                      placeholder="Placeholder text"
                    />
                  </Col>
                </Row>

                {newField.inputType === "select" && (
                  <div className="d-flex align-items-center gap-2 mb-3">
                    <Form.Label className="bootstrap-label">
                      Is Multi Select
                    </Form.Label>
                    <Switch
                      checked={newField.isMultiselect}
                      onCheckedChange={(value) =>
                        setNewField({ ...newField, isMultiselect: value })
                      }
                    />
                  </div>
                )}

                {newField.inputType === "select" && (
                  <div className="mt-3">
                    <Form.Label className="bootstrap-label">
                      Select Options
                    </Form.Label>
                    <CreatableSelectDnd
                      value={newField.options || []}
                      onChange={(opts) => {
                        setNewField({ ...newField, options: opts });
                      }}
                      theme={theme}
                      placeholder={
                        localization.type_an_option ||
                        "Type an option and press Enter..."
                      }
                    />
                    {/* <CreatableSelect
                      isMulti
                      value={(newField.options || []).map((opt) => ({
                        label: opt,
                        value: opt,
                      }))}
                      onChange={(opts) =>
                        setNewField({
                          ...newField,
                          options: opts.map((o) => o.value),
                        })
                      }
                      placeholder="Type an option and press Enter..."
                    /> */}
                  </div>
                )}

                <Form.Group className="mb-3">
                  <Form.Label className="bootstrap-label">
                    Description
                  </Form.Label>
                  <Form.Control
                    value={newField.description}
                    onChange={(e) =>
                      setNewField({ ...newField, description: e.target.value })
                    }
                    placeholder="Description of the field"
                  />
                </Form.Group>

                {newField.inputType === "file-uploader" && (
                  <Row className="mb-2">
                    {/* <Col md={6}> 
                    <div className="d-flex align-items-center gap-2 mt-2">
                      <label className="mb-0 fw-medium">
                        Show Resource Source List
                      </label>

                        <Switch
                          className="ml-8"
                          checked={newField.showResourceSourceOptions}
                          onCheckedChange={(value) =>
                            setNewField({
                              ...newField,
                              showResourceSourceOptions: value,
                            })
                          }
                        />
                      </div>

                      {newField.name && <span
                        className="mt-2 d-inline-flex align-items-center gap-1 px-2 py-1 border border-gray-400 rounded cursor-pointer text-sm ms-auto"
                        onClick={() =>
                          copyToClipboard(`${newField.name}.resourceSourceValue`)
                        }
                        title="Copy field reference"
                        style={{ cursor: "pointer" }}
                      >
                        {"{"+newField.name+".resourceSourceValue}"}
                        <Copy size={12} />
                      </span>}
                    </Col> */}

                    {/* <Col md={6}> 
                    <div className="d-flex align-items-center gap-2 mb-1">
                      <Form.Label className="bootstrap-label">
                        Resource Type
                      </Form.Label>
                    </div>
                    <CustomSelect
                      options={RESOURCE_TYPE_OPTIONS}
                      value={RESOURCE_TYPE_OPTIONS.find((opt) => opt.value === newField.resourceType)}
                      onChange={(opt) =>
                        setNewField({ ...newField, resourceType: opt.value })
                      }
                      placeholder="Select resource type..."
                    />
                    </Col>          */}
                  </Row>
                )}

                <div className="d-flex align-items-center gap-2 mb-3 mt-4">
                  <Form.Label className="bootstrap-label">
                    Required field
                  </Form.Label>
                  <Switch
                    checked={newField.required}
                    onCheckedChange={(value) =>
                      setNewField({ ...newField, required: value })
                    }
                  />
                </div>

                <div className="mt-3 d-flex gap-2">
                  <Button
                    variant="primary"
                    onClick={addVizProp}
                    disabled={!newField.name}
                  >
                    Add Field
                  </Button>
                  <Button
                    variant="outline-secondary"
                    onClick={() => setIsAdding(false)}
                  >
                    Cancel
                  </Button>
                </div>
              </Card.Body>
            </Card>
          )}

          {vizProps.length === 0 ? (
            <div className="text-center py-5 border border-dashed rounded">
              <Box
                size={48}
                className="description-text mb-3 d-block mx-auto"
                style={{ opacity: 0.5 }}
              />
              <h5 className="description-text mb-2">
                No input fields defined yet
              </h5>
              <small className="description-text">
                Click "Add Field" to get started.
              </small>
            </div>
          ) : (
            <DragDropContext
              onDragEnd={(result) => {
                if (!result.destination) return;
                const items = Array.from(vizProps);
                const [reordered] = items.splice(result.source.index, 1);
                items.splice(result.destination.index, 0, reordered);
                onChange(items);
              }}
            >
              <Droppable droppableId="vizProps">
                {(provided) => (
                  <div {...provided.droppableProps} ref={provided.innerRef}>
                    {vizProps.map((prop, idx) => (
                      <Draggable
                        key={idx}
                        draggableId={idx.toString()}
                        index={idx}
                      >
                        {(provided) => (
                          <Accordion
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            expanded={expandedAccordion === idx}
                            onChange={(_, expanded) =>
                              setExpandedAccordion(expanded ? idx : null)
                            }
                            className="mb-3 viz-accordion rounded-xl overflow-hidden"
                          >
                            <AccordionSummary
                              expandIcon={<ExpandMoreIcon />}
                              className="flex items-center w-full"
                            >
                              {/* Drag Handle */}
                              <div
                                {...provided.dragHandleProps}
                                className="mr-2 flex items-center justify-center opacity-50"
                              >
                                <GripVertical size={16} />
                              </div>

                              {/* Label + Subtitle + Badge */}
                              <div className="flex-1 flex flex-col">
                                <Typography className="font-medium">
                                  {prop.label || prop.name}
                                </Typography>

                                <div className="flex items-center gap-2 mt-1">
                                  {/* Type Badge */}
                                  <span className="text-xs font-semibold capitalize bg-blue-100 text-blue-800 border border-blue-200 px-2 py-0.5 rounded-full">
                                    {prop.inputType}
                                  </span>

                                  {/* Field name subtitle */}
                                  <Typography
                                    variant="caption"
                                    className="text-gray-500"
                                  >
                                    {prop.name}
                                  </Typography>
                                </div>
                              </div>

                              {/* Delete Button */}
                              <Button
                                variant="link"
                                size="sm"
                                className="text-danger p-0 d-flex align-items-center justify-content-center"
                                onClick={() => removeVizProp(idx)}
                              >
                                <Trash2 size={16} />
                              </Button>
                            </AccordionSummary>

                            {/* Expanded Form */}
                            <AccordionDetails>
                              <Row className="mb-2">
                                <Col md={6}>
                                  <Form.Label className="bootstrap-label">
                                    Input Type
                                  </Form.Label>
                                  <CustomSelect
                                    options={INPUT_TYPES}
                                    value={INPUT_TYPES.find(
                                      (opt) => opt.value === prop.inputType,
                                    )}
                                    onChange={(opt) =>
                                      updateVizProp(idx, "inputType", opt.value)
                                    }
                                  />
                                </Col>
                                <Col md={6}>
                                  <Form.Label className="bootstrap-label">
                                    Field Name
                                  </Form.Label>
                                  {/* <Form.Control
                                    value={prop.name}
                                    onChange={(e) =>
                                      updateVizProp(idx, "name", e.target.value)
                                    }
                                  /> */}
                                  <Form.Control
                                    // value={prop.name}
                                    disabled={prop.inputType === "student-list"}
                                    value={
                                      prop.inputType === "student-list"
                                        ? "sub_members"
                                        : prop.name
                                    }
                                    onChange={(e) => {
                                      const value = e.target.value
                                        .toLowerCase()
                                        .replace(/\s+/g, "_")
                                        .replace(/[^a-z0-9_]/g, "");

                                      updateVizProp(idx, "name", value);
                                    }}
                                    placeholder="field_name"
                                  />
                                </Col>
                              </Row>

                              <Row className="mb-2">
                                <Col md={6}>
                                  <Form.Label className="bootstrap-label">
                                    Label
                                  </Form.Label>
                                  <Form.Control
                                    value={prop.label}
                                    onChange={(e) =>
                                      updateVizProp(
                                        idx,
                                        "label",
                                        e.target.value,
                                      )
                                    }
                                  />
                                </Col>
                                <Col md={6}>
                                  <Form.Label className="bootstrap-label">
                                    Placeholder
                                  </Form.Label>
                                  <Form.Control
                                    value={prop.placeholder}
                                    onChange={(e) =>
                                      updateVizProp(
                                        idx,
                                        "placeholder",
                                        e.target.value,
                                      )
                                    }
                                  />
                                </Col>
                              </Row>

                              {prop.inputType === "select" && (
                                <div className="d-flex align-items-center gap-2 mb-3">
                                  <Form.Label className="bootstrap-label">
                                    Is Multi Select
                                  </Form.Label>
                                  <Switch
                                    checked={prop.isMultiselect}
                                    onCheckedChange={(value) =>
                                      updateVizProp(idx, "isMultiselect", value)
                                    }
                                  />
                                </div>
                              )}

                              {prop.inputType === "select" && (
                                <div className="mt-3">
                                  <Form.Label className="bootstrap-label">
                                    Select Options
                                  </Form.Label>
                                  <CreatableSelectDnd
                                    value={prop.options || []}
                                    onChange={(opts) => {
                                      updateVizProp(idx, "options", opts);
                                    }}
                                    theme={theme}
                                    placeholder={
                                      localization.type_an_option ||
                                      "Type an option and press Enter..."
                                    }
                                  />
                                  {/* <CreatableSelect
                                    isMulti
                                    value={(prop.options || []).map((opt) => ({
                                      label: opt,
                                      value: opt,
                                    }))}
                                    onChange={(opts) =>
                                      updateVizProp(
                                        idx,
                                        "options",
                                        opts.map((o) => o.value),
                                      )
                                    }
                                    placeholder="Type an option and press Enter..."
                                  /> */}
                                </div>
                              )}

                              <Form.Group className="mb-2">
                                <Form.Label className="bootstrap-label">
                                  Description
                                </Form.Label>
                                <Form.Control
                                  value={prop.description}
                                  onChange={(e) =>
                                    updateVizProp(
                                      idx,
                                      "description",
                                      e.target.value,
                                    )
                                  }
                                />
                              </Form.Group>

                              {prop.inputType === "file-uploader" && (
                                <Row className="mb-2">
                                  {/* <Col md={6}> 
                                  <div className="d-flex align-items-center gap-2 mt-2">
                                    <label className="mb-0 fw-medium">
                                      Show Resource Source Options
                                    </label>
                                    <Switch
                                      className="ml-8"
                                      checked={prop.showResourceSourceOptions}
                                      onCheckedChange={(value) =>
                                        updateVizProp(idx, "showResourceSourceOptions", value)
                                      }
                                    />
                                  </div>
                                  {prop.name && <span
                                    className="mt-2 d-inline-flex align-items-center gap-1 px-2 py-1 border border-gray-400 rounded cursor-pointer text-sm ms-auto"
                                    onClick={() =>
                                      copyToClipboard(`${prop.name}.resourceSourceValue`)
                                    }
                                    title="Copy field reference"
                                    style={{ cursor: "pointer" }}
                                  >
                                    {"{"+prop.name+".resourceSourceValue}"}
                                    <Copy size={12} />
                                  </span>}
                                  </Col>

                                <Col md={6}> 
                                  <Form.Label className="bootstrap-label">
                                    Resource Type
                                  </Form.Label>
                                  <CustomSelect
                                    options={RESOURCE_TYPE_OPTIONS}
                                    value={RESOURCE_TYPE_OPTIONS.find((opt) => opt.value === prop.resourceType)}
                                    onChange={(opt) =>
                                      updateVizProp(idx, "resourceType", opt.value)
                                    }
                                    placeholder="Select resource type..."
                                  />
                                  </Col>          */}
                                </Row>
                              )}

                              <div className="d-flex align-items-center gap-2 mt-4">
                                <label className="mb-0 fw-medium">
                                  Required field
                                </label>
                                <Switch
                                  checked={prop.required}
                                  onCheckedChange={(value) =>
                                    updateVizProp(idx, "required", value)
                                  }
                                />
                              </div>
                            </AccordionDetails>
                          </Accordion>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          )}
        </Card.Body>
      </div>
    </Card>
  );
}
